/**
 * Interactive Data Visualization Components
 * 
 * Pioneering data visualization components for projects:
 * - Animated counter with easing
 * - Circular progress rings with gradients
 * - Interactive chart overlays
 * - Real-time data updates
 * - Smooth transitions and micro-interactions
 */

'use client';

import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/**
 * Animated Counter Component
 * Counts up from 0 to target value with smooth animation
 */
export interface AnimatedCounterProps {
  value: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  className?: string;
}

export function AnimatedCounter({
  value,
  duration = 2,
  suffix = '',
  prefix = '',
  decimals = 0,
  className = '',
}: AnimatedCounterProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      // Easing function (easeOutCubic)
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(value * eased);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration, isInView]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {displayValue.toFixed(decimals)}
      {suffix}
    </span>
  );
}

/**
 * Circular Progress Ring
 * Beautiful animated circular progress indicator
 */
export interface CircularProgressProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  primaryColor?: string;
  secondaryColor?: string;
  label?: string;
  sublabel?: string;
  animate?: boolean;
  showPercentage?: boolean;
  className?: string;
}

export function CircularProgress({
  percentage,
  size = 120,
  strokeWidth = 8,
  primaryColor = '#BBA339',
  secondaryColor = '#e5e7eb',
  label,
  sublabel,
  animate = true,
  showPercentage = true,
  className = '',
}: CircularProgressProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div ref={ref} className={`relative inline-flex flex-col items-center ${className}`}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={secondaryColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        
        {/* Progress Circle with Gradient */}
        <defs>
          <linearGradient id={`gradient-${percentage}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={primaryColor} stopOpacity={1} />
            <stop offset="100%" stopColor={primaryColor} stopOpacity={0.6} />
          </linearGradient>
        </defs>
        
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={`url(#gradient-${percentage})`}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={animate && isInView ? { strokeDashoffset: offset } : {}}
          transition={{ duration: 1.5, ease: 'easeOut', delay: 0.2 }}
        />
      </svg>

      {/* Center Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {showPercentage && (
          <motion.div
            className="text-3xl font-bold"
            style={{ color: primaryColor }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <AnimatedCounter value={percentage} suffix="%" />
          </motion.div>
        )}
        {label && (
          <motion.div
            className="text-xs font-medium text-gray-600"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1 }}
          >
            {label}
          </motion.div>
        )}
      </div>

      {/* Sublabel Below */}
      {sublabel && (
        <motion.div
          className="mt-2 text-sm font-medium text-gray-700"
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 1.2 }}
        >
          {sublabel}
        </motion.div>
      )}
    </div>
  );
}

/**
 * Stat Card with Animation
 * Eye-catching stat display with hover effects
 */
export interface AnimatedStatCardProps {
  value: number;
  label: string;
  icon?: React.ReactNode;
  suffix?: string;
  prefix?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  accentColor?: string;
  className?: string;
}

export function AnimatedStatCard({
  value,
  label,
  icon,
  suffix = '',
  prefix = '',
  trend,
  accentColor = '#BBA339',
  className = '',
}: AnimatedStatCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      className={`group relative overflow-hidden rounded-2xl bg-white p-6 shadow-lg transition-shadow duration-300 hover:shadow-2xl ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.5 }}
    >
      {/* Background Gradient on Hover */}
      <motion.div
        className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-10"
        style={{
          background: `linear-gradient(135deg, ${accentColor} 0%, transparent 100%)`,
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Icon */}
        {icon && (
          <motion.div
            className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl"
            style={{ backgroundColor: `${accentColor}20` }}
            whileHover={{ rotate: 5, scale: 1.1 }}
            transition={{ duration: 0.3 }}
          >
            <div style={{ color: accentColor }}>{icon}</div>
          </motion.div>
        )}

        {/* Value */}
        <motion.div
          className="mb-2 text-4xl font-bold text-gray-900"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <AnimatedCounter value={value} prefix={prefix} suffix={suffix} />
        </motion.div>

        {/* Label */}
        <div className="text-sm font-medium text-gray-600">{label}</div>

        {/* Trend Indicator */}
        {trend && (
          <motion.div
            className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
              trend.isPositive
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
            initial={{ opacity: 0, x: -10 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {trend.isPositive ? (
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            ) : (
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            )}
            {Math.abs(trend.value)}%
          </motion.div>
        )}
      </div>

      {/* Decorative Element */}
      <motion.div
        className="absolute -right-4 -top-4 h-24 w-24 rounded-full opacity-5"
        style={{ backgroundColor: accentColor }}
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </motion.div>
  );
}

/**
 * Progress Bar with Label
 * Animated horizontal progress bar
 */
export interface AnimatedProgressBarProps {
  percentage: number;
  label?: string;
  height?: number;
  primaryColor?: string;
  secondaryColor?: string;
  showPercentage?: boolean;
  animated?: boolean;
  className?: string;
}

export function AnimatedProgressBar({
  percentage,
  label,
  height = 8,
  primaryColor = '#BBA339',
  secondaryColor = '#e5e7eb',
  showPercentage = true,
  animated = true,
  className = '',
}: AnimatedProgressBarProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <div ref={ref} className={`w-full ${className}`}>
      {/* Label and Percentage */}
      {(label || showPercentage) && (
        <div className="mb-2 flex items-center justify-between">
          {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
          {showPercentage && (
            <motion.span
              className="text-sm font-bold"
              style={{ color: primaryColor }}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
            >
              <AnimatedCounter value={percentage} suffix="%" />
            </motion.span>
          )}
        </div>
      )}

      {/* Progress Bar */}
      <div
        className="relative overflow-hidden rounded-full"
        style={{ height, backgroundColor: secondaryColor }}
      >
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: primaryColor }}
          initial={{ width: 0 }}
          animate={animated && isInView ? { width: `${percentage}%` } : {}}
          transition={{ duration: 1.5, ease: 'easeOut', delay: 0.2 }}
        >
          {/* Shimmer Effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
            animate={{
              x: ['-100%', '200%'],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </motion.div>
      </div>
    </div>
  );
}

/**
 * Interactive Comparison Chart
 * Side-by-side bar comparison with animation
 */
export interface ComparisonItem {
  label: string;
  value: number;
  color?: string;
}

export interface AnimatedComparisonChartProps {
  items: ComparisonItem[];
  maxValue?: number;
  accentColor?: string;
  className?: string;
}

export function AnimatedComparisonChart({
  items,
  maxValue,
  accentColor = '#BBA339',
  className = '',
}: AnimatedComparisonChartProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const max = maxValue || Math.max(...items.map((item) => item.value));

  return (
    <div ref={ref} className={`space-y-4 ${className}`}>
      {items.map((item, index) => {
        const percentage = (item.value / max) * 100;
        const barColor = item.color || accentColor;

        return (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            {/* Label and Value */}
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">{item.label}</span>
              <span className="text-sm font-bold" style={{ color: barColor }}>
                {item.value.toLocaleString()}
              </span>
            </div>

            {/* Bar */}
            <div className="h-10 overflow-hidden rounded-lg bg-gray-100">
              <motion.div
                className="flex h-full items-center justify-end px-3 text-xs font-semibold text-white"
                style={{ backgroundColor: barColor }}
                initial={{ width: 0 }}
                animate={isInView ? { width: `${percentage}%` } : {}}
                transition={{ duration: 1, ease: 'easeOut', delay: index * 0.1 + 0.2 }}
                whileHover={{ opacity: 0.9 }}
              >
                {percentage > 20 && `${percentage.toFixed(0)}%`}
              </motion.div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

